<template>
  <div>
      <v-dialog v-model="show" :contained="contained" max-width="800" :persistent="!dataLoaded || activeId !== null" class="rdrive-welcome-dialog" @click:outside="closeMarketingItem">
          <v-card rounded="lg" class="overflow-hidden rdrive-welcome-card" :class="cardClass" :style="cardStyle">

              <!-- Hero: background image with fonted welcome message -->
              <div class="rdrive-welcome-hero" :style="{ backgroundImage: `url(${heroImage})` }">
                  <v-progress-circular v-if="!projectName"
                                       indeterminate
                                       color="white"
                                       size="x-large"
                                       class="mb-4" />
                  <div v-else
                       ref="titleEl"
                       class="rdrive-welcome-hero-title"
                       @dblclick="copyQueryString">
                      {{ t('welcomeMessage', { projectName: projectName }) }}
                  </div>

                  <v-btn v-if="projectName"
                         class="hero-btn text-uppercase"
                         color="#2f9bfe"
                         rounded="pill"
                         size="x-large"
                         variant="flat"
                         :loading="loadingData"
                         :disabled="loadingData || initializing"
                         @click="loadDashboard">
                      {{ t('loadDashboard') }}
                  </v-btn>

                  <!-- Zero-height wrapper: the spinner overflows into the space below the
                       button without shifting the centered title/button at all -->
                  <div v-if="projectName" class="hero-init-spinner">
                      <v-progress-circular v-if="initializing && !loadingData"
                                           indeterminate
                                           color="white"
                                           size="24"
                                           width="2"
                                           class="mt-3" />
                  </div>

                  <!-- Overall progress, overlaid on the bottom of the hero image -->
                  <div v-if="!projectName || loadingData" class="hero-progress">
                      <v-progress-linear :model-value="progressValue"
                                         color="#2f9bfe"
                                         height="6"
                                         class="mb-1"
                                         rounded />
                      <div class="text-caption text-center hero-progress-text text-truncate">
                          {{ apiProgress }} / {{ totalApiRequests }} {{ t('loaded') }}
                      </div>
                  </div>
              </div>

              <template v-if="showMarketingPanel">
                  <!-- Opt-in "Discover what's new from RDrive" panel: everything in this
                       template renders only with showMarketingPanel (its comments included, so
                       with the prop off the card holds the hero and one v-if anchor, nothing
                       else). First the cards, below the hero. -->
                  <div class="rdrive-welcome-discover">
                      <div class="rdrive-welcome-discover-heading mb-4">{{ t('discoverTitle') }}</div>
                      <v-row no-gutters>
                          <v-col v-for="item in panelItems" :key="item.id" cols="3" class="px-2">
                              <div class="rdrive-welcome-discover-card" @click="openMarketingItem(item)">
                                  <div class="rdrive-welcome-discover-thumb mb-2">
                                      <img :src="item.img" :alt="item.title" class="rdrive-welcome-discover-img" />
                                  </div>
                                  <img v-if="item.titleImg"
                                       :src="item.titleImg"
                                       :alt="item.title"
                                       class="rdrive-welcome-discover-logo" />
                                  <div v-else class="rdrive-welcome-discover-title">{{ item.title }}</div>
                                  <div class="rdrive-welcome-discover-text">{{ item.description }}</div>
                              </div>
                          </v-col>
                      </v-row>
                  </div>

                  <!-- The item pages, parked to the right of the welcome view; opening a card
                       slides them in. All of them render up front so they preload in the
                       background — a click only reveals the already-loaded one. Each iframe keeps
                       its full viewport height (item.height) so the page lays out correctly; the
                       crop window hides the page's empty bottom strip. -->
                  <div class="rdrive-welcome-item-panel">
                      <div class="d-flex align-center pa-3 flex-shrink-0">
                          <v-btn icon="mdi-arrow-left"
                                 variant="text"
                                 size="small"
                                 class="mr-2"
                                 @click="closeMarketingItem" />
                          <span class="text-body-1 font-weight-medium">{{ activeItem?.title }}</span>
                      </div>
                      <v-divider />
                      <div class="rdrive-welcome-item-crop" :style="{ height: cropHeight + 'px' }">
                          <iframe v-for="item in panelItems"
                                  :key="item.id"
                                  :src="item.url"
                                  class="rdrive-welcome-item-frame"
                                  :style="{
                                      height: item.height + 'px',
                                      visibility: item.id === activeId ? 'visible' : 'hidden'
                                  }"
                                  frameborder="0"
                                  scrolling="no"
                                  @load="onItemFrameLoad(item, $event)" />
                      </div>
                  </div>
              </template>

          </v-card>
      </v-dialog>

      <!-- Toast Message -->
      <v-snackbar v-model="showToast"
                  :timeout="2000"
                  color="success"
                  location="top">
          {{ t('devModeActivated') }}
      </v-snackbar>

      <!-- Error Toast for Copy Failure -->
      <v-snackbar v-model="showErrorToast"
                  :timeout="3000"
                  color="error"
                  location="top">
          {{ t('copyFailed') }}
      </v-snackbar>
  </div>
</template>

<script src="./model.js"></script>
<style src="./style.css"></style>
